import { Controller } from "@hotwired/stimulus"

// The thread header's Participants roster. A nested panel rather than one parented to the body,
// unlike contact_popover and message_menu: those open from cards inside .thread__scroll, which
// clips a descendant; this opens from the header, which is outside it.
//
// It cannot reuse dropdown_controller either — that one submits a toolbar form on select and
// closes on any click inside itself, and this panel holds a Subscribe button whose response
// re-renders the panel in place.
//
// Open state lives in the markup (the panel's `hidden` attribute), not in a field here. That is
// what lets a subscribe come back still open: the Turbo Stream replaces this whole element, and
// the controller reconnects onto a panel that is already up rather than resetting it.
export default class extends Controller {
  static targets = [ "panel", "trigger" ]

  connect() {
    this.closeOnOutside = this.closeOnOutside.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)

    document.addEventListener("click", this.closeOnOutside)
    document.addEventListener("keydown", this.closeOnEscape)

    // Only ever true on the re-render after a subscribe — a first paint is closed. The press
    // that caused it took its own button down with it, so focus is on <body> by now and the
    // keyboard reader has lost their place unless it is put back.
    if (this.open) this.subscribeButton?.focus({ preventScroll: true })
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  toggle() {
    this.open ? this.close() : this.show()
  }

  // Not named `open`: that reads as the state this controller asks about everywhere else.
  show() {
    this.panelTarget.hidden = false
    this.markExpanded()
  }

  close() {
    if (!this.open) return

    this.panelTarget.hidden = true
    this.markExpanded()
  }

  closeOnOutside(event) {
    if (!this.open || this.element.contains(event.target)) return

    this.close()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || !this.open) return

    this.close()
    this.triggerTarget.focus({ preventScroll: true })
  }

  markExpanded() {
    this.triggerTarget.setAttribute("aria-expanded", String(this.open))
  }

  get open() {
    return !this.panelTarget.hidden
  }

  get subscribeButton() {
    return this.panelTarget.querySelector(".thread-participants__toggle")
  }
}
